import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X } from "lucide-react";
import {
  Heart,
  HeartPulse,
  Activity,
  Leaf,
  Flower,
  Flower2,
  TreeDeciduous,
  Sun,
  Moon,
  Sparkles,
  Zap,
  Wind,
  Droplets,
  Droplet,
  Brain,
  Eye,
  Hand,
  Home,
  SprayCan,
  Bath,
  Bed,
  Baby,
  Dog,
  Cat,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Clock,
  Flame,
  Snowflake,
  CloudSun,
  Waves,
  Mountain,
  Smile,
  Frown,
  Meh,
  ThermometerSun,
  Pill,
  Syringe,
  Stethoscope,
  Apple,
  Coffee,
  type LucideIcon,
} from "lucide-react";

// Map of icon names to components
export const AVAILABLE_ICONS = {
  Heart,
  HeartPulse,
  Activity,
  Leaf,
  Flower,
  Flower2,
  TreeDeciduous,
  Sun,
  Moon,
  Sparkles,
  Zap,
  Wind,
  Droplets,
  Droplet,
  Brain,
  Eye,
  Hand,
  Home,
  SprayCan,
  Bath,
  Bed,
  Baby,
  Dog,
  Cat,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Clock,
  Flame,
  Snowflake,
  CloudSun,
  Waves,
  Mountain,
  Smile,
  Frown,
  Meh,
  ThermometerSun,
  Pill,
  Syringe,
  Stethoscope,
  Apple,
  Coffee,
} as const satisfies Record<string, LucideIcon>;

export function resolveAvailableIcon(iconName?: string | null): LucideIcon | null {
  if (!iconName) return null;
  const icon = (AVAILABLE_ICONS as Record<string, LucideIcon | undefined>)[iconName];
  return icon ?? null;
}

export function resolveAvailableIconOr(
  iconName: string | null | undefined,
  fallback: LucideIcon
): LucideIcon {
  return resolveAvailableIcon(iconName) ?? fallback;
}

interface IconSelectorProps {
  value?: string;
  onChange: (iconName: string | undefined) => void;
}

export function IconSelector({ value, onChange }: IconSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const iconNames = Object.keys(AVAILABLE_ICONS);
  
  const SelectedIcon = resolveAvailableIcon(value);

  return (
    <div className="space-y-2">
      {/* Selected icon display / toggle button */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "flex items-center justify-center gap-2 rounded-lg border-2 p-3 transition-colors min-h-[48px] min-w-[48px]",
            "hover:border-primary/50 hover:bg-muted/50",
            value ? "border-primary bg-primary/5" : "border-dashed border-muted-foreground/30"
          )}
        >
          {SelectedIcon ? (
            <>
              <SelectedIcon className="h-5 w-5" />
              <span className="text-sm">{value}</span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">Selecionar ícone</span>
          )}
        </button>
        
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(undefined)}
            className="h-10 px-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Limpar</span>
          </Button>
        )}
      </div>

      {/* Icon grid */}
      {isExpanded && (
        <ScrollArea className="h-48 rounded-lg border bg-muted/20 p-2">
          <div className="grid grid-cols-6 gap-1 sm:grid-cols-8">
            {iconNames.map((iconName) => {
              const IconComponent = AVAILABLE_ICONS[iconName];
              const isSelected = value === iconName;
              
              return (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => {
                    onChange(iconName);
                    setIsExpanded(false);
                  }}
                  className={cn(
                    "flex items-center justify-center rounded-md p-2 transition-colors",
                    "min-h-[44px] min-w-[44px]",
                    "hover:bg-primary/10",
                    isSelected && "bg-primary text-primary-foreground"
                  )}
                  title={iconName}
                >
                  <IconComponent className="h-5 w-5" />
                </button>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
